/**
 * Guest → host debug telemetry over the data channel (DEBUG BUILD ONLY).
 *
 * The guest measures stats (and occasionally hits odd states) that only exist in its own
 * console. Instead of requiring a phone console capture, the guest forwards diagnostics to
 * the host as a datachannel message; the host logs them next to its own recovery logs, so a
 * field session can be replayed from ONE console — the host's.
 *
 * The guest only sends these while its page is loaded with `?debug` (or the
 * PLAYCAST_DEBUG_WEBRTC env var is set), so production guests never emit the message. The
 * host, however, ACCEPTS the message unconditionally — a debug guest connecting to a
 * stock host must still be audible, and a few extra lines of console output on the host is
 * the entire cost. Sending these is rate-limited to the same 2s cadence as clientStats.
 */

export const GUEST_DEBUG_MESSAGE_TYPE = 'guestDebug' as const;

export type GuestDebugKind = 'info' | 'error';

export type GuestDebugMessage = {
  type: typeof GUEST_DEBUG_MESSAGE_TYPE;
  kind: GuestDebugKind;
  /** Free-form diagnostic detail; the host logs it verbatim. */
  detail: Record<string, unknown>;
};

/** Structural guard for the wire message (host side). */
export function isGuestDebugMessage(value: unknown): value is GuestDebugMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg['type'] !== GUEST_DEBUG_MESSAGE_TYPE) return false;
  const kind = msg['kind'];
  if (kind !== 'info' && kind !== 'error') return false;
  return typeof msg['detail'] === 'object' && msg['detail'] !== null;
}